import React from "react";
import { Container, Typography, Box, Card, Grid, Button, CardContent, CardMedia } from "@mui/material";
import { styled } from "@mui/system";
import { motion } from "framer-motion";
//import CloudIcon from '@mui/icons-material/Cloud';
import StorageIcon from '@mui/icons-material/Storage';
import SecurityIcon from '@mui/icons-material/Security';
import MemoryIcon from '@mui/icons-material/Memory';
//import DnsIcon from '@mui/icons-material/Dns';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';

const categories = [
  {
    category: "Compute & Containers",
    icon: <MemoryIcon fontSize="large" sx={{ color: '#FACD00' }} />,
    features: [
      {
        title: "Docker Container",
        description: "Automated container orchestration for deploying, managing, and scaling your containerized applications with Google's production-grade infrastructure.",
        imgSrc: 'https://images.unsplash.com/photo-1667372393119-c81c0cda1a89?auto=format&fit=crop&w=600&q=80'
      },
      {
        title: "Cloud Run",
        description: "Develop and deploy highly scalable containerized applications on a fully managed serverless platform. Zero infrastructure management required.",
        imgSrc: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=600&q=80'
      },
      {
        title: "Compute Engine",
        description: "High-performance virtual machines tailored to your specific needs. Run large-scale workloads on secure, reliable infrastructure.",
        imgSrc: 'https://images.unsplash.com/photo-1558494949-ef2bb6db8744?auto=format&fit=crop&w=600&q=80'
      },
    ]
  },
  {
    category: "Storage & Databases",
    icon: <StorageIcon fontSize="large" sx={{ color: '#FACD00' }} />,
    features: [
      {
        title: "Cloud Storage",
        description: "Secure, scalable, and durable object storage for data of any size. Perfect for backups, serving web content, and data lakes.",
        imgSrc: "https://images.unsplash.com/photo-1544197150-b99a580bbcbf?auto=format&fit=crop&w=600&q=80"
      },
      {
        title: "MongoDB",
        description: "Fully managed NoSQL database services for MongoDB. Automated backups, replication, and failover.",
        imgSrc: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=600&q=80'
      },
    ]
  },
  {
    category: "Networking & Security",
    icon: <SecurityIcon fontSize="large" sx={{ color: '#FACD00' }} />,
    features: [
      {
        title: "Cloud DNS",
        description: "Reliable, resilient, low-latency DNS serving from Google's worldwide network. 100% availability SLA for your domain names.",
        imgSrc: 'https://images.unsplash.com/photo-1558494949-ef2bb6db8744?auto=format&fit=crop&w=600&q=80'
      },
      {
        title: "Cloud Load Balancing",
        description: "Scale your applications on Google Cloud. Distribute your load-balanced compute resources in single or multiple regions.",
        imgSrc: 'https://images.unsplash.com/photo-1558494949-ef2bb6db8744?auto=format&fit=crop&w=600&q=80'
      },
      {
        title: "Cloud Armor",
        description: "Protect your applications and websites against denial of service (DoS) and web attacks with enterprise-grade security policies.",
        imgSrc: 'https://images.unsplash.com/photo-1563206767-5b1d972e8136?auto=format&fit=crop&w=600&q=80'
      },

    ]
  },
  {
    category: "AI & Analytics",
    icon: <AutoGraphIcon fontSize="large" sx={{ color: '#FACD00' }} />,
    features: [
      {
        title: "Vertex AI",
        description: "Build, deploy, and scale machine learning models faster with fully managed ML tools for any use case.",
        imgSrc: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&w=600&q=80'
      },
      {
        title: "BigQuery",
        description: "Serverless, highly scalable, and cost-effective multi-cloud data warehouse designed for business agility.",
        imgSrc: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=600&q=80'
      },
    ]
  },

];

const CategoryContainer = styled(Box)({
  marginBottom: "80px",
});

const CategoryHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginBottom: "30px",
  gap: "15px",
  borderBottom: "2px solid #333",
  paddingBottom: "10px",
});

const FeatureCard = styled(Card)({
  height: '100%',
  display: "flex",
  flexDirection: "column",
  transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: "0 12px 20px rgba(0,0,0,0.2)",
  },
  borderRadius: "16px",
  overflow: "hidden",
  backgroundColor: "#fff",
});

const FeatureTitle = styled(Typography)({
  fontSize: "1.25rem",
  fontWeight: "600",
  marginBottom: "10px",
});

const FeatureDescription = styled(Typography)({
  fontSize: "0.95rem",
  color: "#555",
  lineHeight: 1.6,
});


const Ecosystem = () => {
  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          position: "relative",
          height: { xs: "400px", sm: "500px", md: "500px" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 4,
          overflow: "hidden",
          mt: 4,
          mb: 8,
          boxShadow: 4,
        }}
      >
        {/* Background Image Layer */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1600&q=80')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "brightness(0.7)",
            zIndex: -1
          }}
        />

        {/* Hero Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          style={{
            textAlign: "center",
            padding: "20px",
            zIndex: 1,
            maxWidth: "800px"
          }}
        >
          <Typography variant="h2" fontWeight="800" color="white" sx={{ textShadow: "0px 4px 10px rgba(0,0,0,0.5)", fontSize: { xs: "2.5rem", md: "4rem" } }}>
            Open Source Solutions
          </Typography>
          <Typography variant="h5" color="rgba(255,255,255,0.9)" mt={2} sx={{ fontWeight: 400 }}>
            Architecting the future with Open Source Solutions.
          </Typography>

          <Box mt={4} display="flex" justifyContent="center" gap={2}>
            <Button variant="contained" size="large" sx={{ backgroundColor: '#FACD00', color: '#000', fontWeight: 'bold', px: 4, py: 1.5 }}>
              Start Building
            </Button>
            <Button variant="outlined" size="large" sx={{ borderColor: '#fff', color: '#fff', fontWeight: 'bold', px: 4, py: 1.5, '&:hover': { borderColor: '#FACD00', color: '#FACD00' } }}>
              View Solutions
            </Button>
          </Box>
        </motion.div>
      </Box>

      {/* Categories Loop */}
      {categories.map((category, index) => (
        <CategoryContainer key={index}>
          <CategoryHeader>
            {category.icon}
            <Typography variant="h4" fontWeight="bold" color="textPrimary">
              {category.category}
            </Typography>
          </CategoryHeader>
          <Grid container spacing={4}>
            {category.features.map((feature, featureIndex) => (
              <Grid item xs={12} sm={6} md={4} key={featureIndex}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: featureIndex * 0.1 }}
                  style={{ height: '100%' }}
                >
                  <FeatureCard elevation={2}>
                    <CardMedia
                      component="img"
                      height="200"
                      image={feature.imgSrc}
                      alt={feature.title}
                    />
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <FeatureTitle variant="h6">{feature.title}</FeatureTitle>
                      <FeatureDescription variant="body2">
                        {feature.description}
                      </FeatureDescription>
                    </CardContent>
                    <Box sx={{ p: 2, pt: 0 }}>
                      <Button size="small" sx={{ color: '#000', fontWeight: 'bold' }}>Learn More</Button>
                    </Box>
                  </FeatureCard>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </CategoryContainer>
      ))}
    </Container>
  );
};

export default Ecosystem;